import React, { useEffect, useState } from "react";
import InputBox from "../../../components/InputBox";
import useCustomer from "../../../hooks/useCustomer";
import { notifySuccess } from "../../../../../assets/toastService";
import { Button } from "src/business-types/grocery/components/Button";
import { FontColor, ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";
import { errorHandling } from "src/shared/services/errorHandling";
import { CustomerData } from "src/shared/services/interfacev2";
import { getLedgerByCustomer, postCustomer } from "src/shared/api";
import { Product } from "../NotesProducts";

type Props = {
  customerDetails: (data: CustomerData | "") => void;
  clearData: string;
  selectProductList: Product[];
};

const CustomerSelection: React.FC<Props> = ({
  customerDetails,
  clearData,
  selectProductList,
}) => {
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMore, setShowMore] = useState<boolean>(false);
  const [foundCustomer, setFoundCustomer] = useState<CustomerData | null>(null);
  const [reFresh, setReFresh] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const [formData, setFormData] = useState({
    customeraddress1: "",
    customeraddress2: "",
    city: "",
    state: "",
    postalCode: "",
    Email: "",
    osb: "",
  });

  const [error, setError] = useState({
    Email: "",
    phoneNumber: "",
  });

  const { onlyCustomer } = useCustomer(
    reFresh,
    false,
    1,
    10,
    searchTerm.length > 2 ? searchTerm : ""
  );

  const matchingCustomers = searchTerm.length > 2
    ? onlyCustomer.filter((customer) =>
        [customer.firstName, customer.middleName, customer.lastName]
          .filter(Boolean).join(" ").toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phones.some((phone) => phone.phone.includes(searchTerm))
      )
    : [];

  /** 📧 Email Validation */
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (email && !emailRegex.test(email)) {
      setError((prev) => ({
        ...prev,
        Email: "Invalid email address",
      }));
      return false;
    }

    setError((prev) => ({
      ...prev,
      Email: "",
    }));

    return true;
  };

  /** 📞 Phone Change */
  const handlePhoneChange = (number: string) => {
    const sanitized = number.trim().replace(/\D/g, "");

    if (sanitized.length <= 12) {
      setPhoneNumber(sanitized);
      setSearchTerm(sanitized);
      setFoundCustomer(null);
      if (foundCustomer) setName("");
    }

    // Validation
    if (sanitized.length > 0 && sanitized.length < 7) {
      setError((prev) => ({
        ...prev,
        phoneNumber: "Phone number must be at least 7 digits",
      }));
    } else {
      setError((prev) => ({
        ...prev,
        phoneNumber: "",
      }));
    }

    if (sanitized.length < 4) {
      setFoundCustomer(null);
    }
  };

  /** 🔍 Auto Select Customer */
  useEffect(() => {
    const exactCustomer = onlyCustomer.find((customer) =>
      customer.phones.some((phone) => phone.phone === phoneNumber)
    );
    if (phoneNumber.length >= 7 && searchTerm === phoneNumber && exactCustomer) {
      handleSelectCustomer(exactCustomer);
    } else if (phoneNumber === "0000000000" && !foundCustomer) {
      setName("XXXX");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, onlyCustomer, searchTerm]);

  /** 👤 Select Existing Customer */
  const handleSelectCustomer = async (
    selected: CustomerData
  ) => {
    setError({
      Email: "",
      phoneNumber: "",
    });
    setFoundCustomer(selected);
    setPhoneNumber(selected.phones[0]?.phone ?? "");
    setName(selected.firstName);
    setSearchTerm("");

    try {
      const ledger = await getLedgerByCustomer(
        selected._id.$oid
      );
      setFoundCustomer((current) => current?._id.$oid === selected._id.$oid
        ? {
            ...current,
            totalOutstandingBalance: ledger.data?.ledger?.totalOutstandingBalance,
          }
        : current);
    } catch {
      // Keep the selected customer when the balance is unavailable.
    }
  };

  /** 💾 Add Customer */
  const handleConfirmNew = async () => {
    // Phone validation
    if (phoneNumber.length < 7) {
      setError((prev) => ({
        ...prev,
        phoneNumber: "Phone number must be at least 7 digits",
      }));
      return;
    }

    // Email validation
    if (!validateEmail(formData.Email)) {
      return;
    }

    const newCustomer = {
      firstName: name,
      middleName: "",
      lastName: "",
      emails: formData.Email ? [formData.Email] : [],
      phones: [phoneNumber],
      addresses: {
        addressLine1: formData.customeraddress1,
        addressLine2: formData.customeraddress2,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
      },
      openingOutstandingBalance:
        Number(formData.osb) ?? 0,
    };

    try {
      await postCustomer(newCustomer);

      notifySuccess("Customer added successfully");

      setReFresh(!reFresh);
      setShowMore(false);

      setError({
        Email: "",
        phoneNumber: "",
      });
    } catch (err) {
      errorHandling(err);
      console.error(err);
    }
  };

  /** 🧹 Reset Form */
  const resetForm = () => {
    setFormData({
      customeraddress1: "",
      customeraddress2: "",
      city: "",
      state: "",
      postalCode: "",
      Email: "",
      osb: "",
    });

    setPhoneNumber("");
    setSearchTerm("");
    setName("");
    setFoundCustomer(null);

    setError({
      Email: "",
      phoneNumber: "",
    });

    customerDetails("");
  };

  /** 🔄 External Clear */
  useEffect(() => {
    resetForm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearData]);

  /** 📤 Send Data to Parent */
  useEffect(() => {
    customerDetails(foundCustomer || "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foundCustomer]);

  /** 🧼 Auto Clear */
  useEffect(() => {
    if (selectProductList.length === 1) {
      resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectProductList]);

  /** 🧱 Common Input */
  const renderInput = (
    label: string,
    value: string,
    key: keyof typeof formData
  ) => (
    <div key={key}>
      <div className='w-full p-2 flex justify-center items-center gap-2'>
        <p className='w-2/6'>{label}</p>

        <InputBox
          type='text'
          value={value}
          disabled={!!foundCustomer}
          maxLength={50}
          onChange={(e) => {
            const inputValue = e.target.value;

            setFormData((prev) => ({
              ...prev,
              [key]: inputValue,
            }));

            if (key === "Email") {
              validateEmail(inputValue);
            }
          }}
        />
      </div>

      {key === "Email" && error.Email && (
        <p className='text-end text-red-400 text-sm'>
          {error.Email}
        </p>
      )}
    </div>
  );

  return (
    <div className='w-full bg-white border border-dashed border-black p-1 rounded-lg shadow-md'>
      {/* 🔹 Header */}
      <div
        className={`w-full py-1 text-lg font-bold rounded-xl flex justify-between items-center px-3 border ${ThemeBasedColorHeader()}`}
      >
        <div className='flex gap-2'>
          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className='cursor-pointer'
          >
            {isOpen ? "▲" : "▼"}
          </button>

          <h1>Customer Selection</h1>
        </div>

        {!phoneNumber && (
          <button
            onClick={() => {
              setPhoneNumber("0000000000");
              setSearchTerm("0000000000");
            }}
            className={`border bg-white shadow-md py-1 px-2 rounded-md ${FontColor()} disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={selectProductList.length === 1}
          >
            Unknown Customer
          </button>
        )}
      </div>

      {isOpen && (
        <>
          {/* 📞 Phone */}
          <div>
            <div className='w-full p-2 flex justify-center items-center gap-2'>
              <p className='w-2/6'>Ph. Number</p>

              <InputBox
                type='tel'
                placeholder='Search or Add Ph.Number'
                value={
                  foundCustomer?.phones[0]?.phone ??
                  phoneNumber
                }
                onChange={(e) =>
                  handlePhoneChange(e.target.value)
                }
                min={0}
                maxLength={12}
                disabled={selectProductList.length === 1}
              />
            </div>

            {error.phoneNumber && (
              <p className='text-end text-red-400 text-sm'>
                {error.phoneNumber}
              </p>
            )}
          </div>

          {/* 👤 Name */}
          <div className='w-full relative p-2 flex justify-center items-center gap-2'>
            <p className='w-2/6'>Name</p>

            <div className='w-full relative'>
              <InputBox
                type='text'
                placeholder='Search or Add Customer Name'
                value={foundCustomer?.firstName ?? name}
                onChange={(e) => {
                  setName(e.target.value);
                  setSearchTerm(e.target.value.trim());
                  setFoundCustomer(null);
                  if (foundCustomer) setPhoneNumber("");
                }}
                disabled={
                  selectProductList.length === 1 ||
                  name === "XXXX"
                }
                maxLength={50}
              />

              {/* 🔽 Customer Dropdown */}
              {searchTerm.length > 2 &&
                !foundCustomer && (
                  <div className='absolute z-10 w-full bg-white border border-gray-300 mt-1 rounded-md shadow-lg max-h-40 overflow-y-auto'>
                    {matchingCustomers.map(
                      (cust: CustomerData, index: number) => (
                        <div
                          key={index}
                          onClick={() =>
                            handleSelectCustomer(cust)
                          }
                          className='p-2 cursor-pointer hover:bg-blue-100'
                        >
                          <p className='font-semibold'>
                            {[cust.firstName, cust.middleName, cust.lastName].filter(Boolean).join(" ")}
                          </p>

                          <p className='text-sm text-gray-500'>
                            {cust.phones
                              .map((p) => p.phone)
                              .join(", ")}
                          </p>
                        </div>
                      )
                    )}

                    {matchingCustomers.length === 0 && (
                      <div className='p-2 text-gray-500 text-center'>
                        No customer found
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>

          {/* ➕ Additional Fields */}
          {!foundCustomer && (
            <div className='w-full px-2'>
              <div className='w-full flex justify-end'>
                {selectProductList.length !== 1 && (
                  <p
                    className='cursor-pointer text-blue-500 hover:underline hover:text-blue-700'
                    onClick={() =>
                      setShowMore(!showMore)
                    }
                  >
                    {showMore
                      ? "Hide"
                      : "+ More Details"}
                  </p>
                )}
              </div>

              {showMore && (
                <div>
                  {/* Previous Balance */}
                  <div className='w-full p-2 flex justify-center items-center gap-2'>
                    <p className='w-2/6'>Prv. Balance</p>

                    <InputBox
                      type='number'
                      placeholder='Prev. Balance'
                      value={formData.osb}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          osb: e.target.value,
                        }))
                      }
                      min={0}
                      maxLength={12}
                    />
                  </div>

                  {/* Email */}
                  {renderInput(
                    "Email",
                    formData.Email,
                    "Email"
                  )}

                  <p className='text-start mt-2'>
                    Address :
                  </p>

                  {renderInput(
                    "Line 1",
                    formData.customeraddress1,
                    "customeraddress1"
                  )}

                  {renderInput(
                    "Line 2",
                    formData.customeraddress2,
                    "customeraddress2"
                  )}

                  {renderInput(
                    "City",
                    formData.city,
                    "city"
                  )}

                  {renderInput(
                    "State",
                    formData.state,
                    "state"
                  )}

                  {renderInput(
                    "Postal Code",
                    formData.postalCode,
                    "postalCode"
                  )}
                </div>
              )}

              {/* Confirm Button */}
              <div className='w-full flex justify-center items-center mt-2'>
                <Button
                  label='Confirm'
                  onClick={handleConfirmNew}
                  variant='ThemeBase'
                  disabled={
                    phoneNumber.length < 7 ||
                    !!error.Email
                  }
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerSelection;